/**
 * SmartLink Router operator page.
 *
 * Surfaces FS-101 (single-URL multi-offer routing with policy + eligibility)
 * in the dash:
 *   - create-smartlink form     (slug, name, policy)                  react-hook-form + zod
 *   - attach-candidate form     (offer_id, weight, payout, active,
 *                                geo/device/os eligibility rules)     react-hook-form + zod
 *   - smartlinks list           (fetched from GET /)
 *   - candidates sub-table      (per selected smartlink)
 *   - live resolve-by-slug      (POST /resolve + optional context)
 *   - sample distribution       (legacy preview via /:id/resolve)
 *
 * Wires to `/api/v1/smartlink-router` via ./api.ts. Each fetch sends the
 * `x-tenant-id: demo` header through the root `apiClient`.
 *
 * Brand rules honored:
 *   - emerald→sky gradient ONLY on the headline via `text-brand-gradient`
 *   - Inter font (inherited from body)
 *   - zinc borders, 6–8 px rounded corners
 *   - light theme, no emojis, no competitor names
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import type { z } from "zod";
import {
	attachCandidate,
	attachCandidateSchema,
	createSmartlink,
	createSmartlinkSchema,
	deleteSmartlink,
	formatEligibility,
	formatPolicyLabel,
	listSmartlinks,
	MAX_PAYOUT_CENTS,
	MAX_WEIGHT,
	patchSmartlink,
	removeCandidate,
	resolveBySlug,
	resolveContextSchema,
	resolvePreview,
	SMARTLINK_POLICIES,
	smartlinksRouterQueryKeys,
	tallyDistribution,
	type AttachCandidateInput,
	type CreateSmartlinkInput,
	type ResolveContextInput,
	type ResolveResponse,
	type SmartLinkCandidateRow,
	type SmartLinkRow,
} from "./api";

// ---------------------------------------------------------------------------
// Static style tokens. Kept at module scope so the JSX stays readable.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 hover:border-red-300 disabled:cursor-not-allowed disabled:opacity-60";

const RESOLVE_SAMPLE_SIZE = 10;

// ---------------------------------------------------------------------------
// Page entry.
// ---------------------------------------------------------------------------

export function SmartlinksRouterPage() {
	const queryClient = useQueryClient();

	const listQuery = useQuery({
		queryKey: smartlinksRouterQueryKeys.list(),
		queryFn: listSmartlinks,
		// The API has no realtime surface yet — keep it simple and refetch on
		// mount + after every mutation via manual invalidation.
	});

	const [attachmentsByLink, setAttachmentsByLink] = useState<
		Record<string, SmartLinkCandidateRow[]>
	>({});
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [preview, setPreview] = useState<ResolveResponse[] | null>(null);
	const [testResult, setTestResult] = useState<ResolveResponse | null>(null);

	// Pick the first smartlink once the list loads.
	useEffect(() => {
		if (
			selectedId === null &&
			listQuery.data &&
			listQuery.data.length > 0
		) {
			setSelectedId(listQuery.data[0]!.id);
		}
	}, [listQuery.data, selectedId]);

	// ---------------- Forms ----------------
	// zod v4 schemas with .default() produce divergent input/output types; RHF v7
	// needs the input type for defaults and the output type for submit. The
	// three-generic form keeps @hookform/resolvers v5 happy.
	const createForm = useForm<
		z.input<typeof createSmartlinkSchema>,
		unknown,
		z.output<typeof createSmartlinkSchema>
	>({
		resolver: zodResolver(createSmartlinkSchema),
		defaultValues: { slug: "", name: "", policy: "weighted" },
		mode: "onSubmit",
	});
	const attachForm = useForm<
		z.input<typeof attachCandidateSchema>,
		unknown,
		z.output<typeof attachCandidateSchema>
	>({
		resolver: zodResolver(attachCandidateSchema),
		defaultValues: {
			offer_id: "",
			weight: 1,
			payout_cents: 0,
			active: true,
			eligibility_geo: "",
			eligibility_device: "",
			eligibility_os: "",
		},
		mode: "onSubmit",
	});
	const testForm = useForm<
		z.input<typeof resolveContextSchema>,
		unknown,
		z.output<typeof resolveContextSchema>
	>({
		resolver: zodResolver(resolveContextSchema),
		defaultValues: { slug: "", geo: "", device: "", os: "" },
		mode: "onSubmit",
	});

	// ---------------- Mutations ----------------
	const createMutation = useMutation({
		mutationFn: createSmartlink,
		onSuccess: (row) => {
			toast.success(`SmartLink "${row.slug}" created.`);
			queryClient.invalidateQueries({
				queryKey: smartlinksRouterQueryKeys.list(),
			});
			setSelectedId(row.id);
			setPreview(null);
			createForm.reset({ slug: "", name: "", policy: "weighted" });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to create smartlink.");
		},
	});

	const attachMutation = useMutation({
		mutationFn: async (input: AttachCandidateInput) => {
			if (!selectedId) throw new Error("no_smartlink_selected");
			return attachCandidate(selectedId, input);
		},
		onSuccess: (row) => {
			toast.success(`Candidate "${row.offer_id}" attached.`);
			setAttachmentsByLink((prev) => ({
				...prev,
				[row.smartlink_id]: [...(prev[row.smartlink_id] ?? []), row],
			}));
			attachForm.reset({
				offer_id: "",
				weight: 1,
				payout_cents: 0,
				active: true,
				eligibility_geo: "",
				eligibility_device: "",
				eligibility_os: "",
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to attach candidate.");
		},
	});

	const patchMutation = useMutation({
		mutationFn: async (args: {
			id: string;
			input: { active?: boolean; policy?: SmartLinkRow["policy"] };
		}) => patchSmartlink(args.id, args.input),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: smartlinksRouterQueryKeys.list(),
			});
			toast.success("Updated.");
		},
		onError: (err: Error) => toast.error(err.message || "Update failed."),
	});

	const deleteMutation = useMutation({
		mutationFn: async (id: string) => deleteSmartlink(id),
		onSuccess: (_void, id) => {
			setAttachmentsByLink((prev) => {
				const copy = { ...prev };
				delete copy[id];
				return copy;
			});
			if (selectedId === id) setSelectedId(null);
			setPreview(null);
			queryClient.invalidateQueries({
				queryKey: smartlinksRouterQueryKeys.list(),
			});
			toast.success("Deleted.");
		},
		onError: (err: Error) => toast.error(err.message || "Delete failed."),
	});

	const removeCandidateMutation = useMutation({
		mutationFn: async (args: { smartlinkId: string; candidateId: string }) =>
			removeCandidate(args.smartlinkId, args.candidateId),
		onSuccess: (_void, args) => {
			setAttachmentsByLink((prev) => ({
				...prev,
				[args.smartlinkId]: (prev[args.smartlinkId] ?? []).map((c) =>
					c.id === args.candidateId ? { ...c, active: 0 } : c,
				),
			}));
			toast.success("Candidate paused.");
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to pause candidate."),
	});

	const previewMutation = useMutation({
		mutationFn: async (id: string) => resolvePreview(id, RESOLVE_SAMPLE_SIZE),
		onSuccess: (rows) => {
			setPreview(rows);
			queryClient.invalidateQueries({
				queryKey: smartlinksRouterQueryKeys.resolve(
					selectedId ?? "_unknown",
				),
			});
			toast.success(`Pulled ${rows.length} resolves.`);
		},
		onError: (err: Error) => {
			setPreview(null);
			toast.error(err.message || "Resolve preview failed.");
		},
	});

	const testMutation = useMutation({
		mutationFn: resolveBySlug,
		onSuccess: (row) => {
			setTestResult(row);
			toast.success(`Resolved → ${row.offer_id ?? row.picked.offer_id}`);
		},
		onError: (err: Error) => {
			setTestResult(null);
			toast.error(err.message || "Resolve failed.");
		},
	});

	// ---------------- Handlers ----------------
	const onCreate: SubmitHandler<CreateSmartlinkInput> = (values) => {
		createMutation.mutate(values);
	};
	const onAttach: SubmitHandler<AttachCandidateInput> = (values) => {
		if (!selectedId) {
			toast.error("Create or select a smartlink first.");
			return;
		}
		attachMutation.mutate(values);
	};
	function onPreview() {
		if (!selectedId) {
			toast.error("Select a smartlink to resolve.");
			return;
		}
		previewMutation.mutate(selectedId);
	}
	const onTestResolve: SubmitHandler<ResolveContextInput> = (values) => {
		testMutation.mutate(values);
	};

	const smartlinks = listQuery.data ?? [];
	const selected = useMemo(
		() => smartlinks.find((l) => l.id === selectedId) ?? null,
		[smartlinks, selectedId],
	);
	const selectedAttachments = selectedId
		? (attachmentsByLink[selectedId] ?? [])
		: [];
	const distribution = useMemo(
		() => (preview ? tallyDistribution(preview) : []),
		[preview],
	);

	// ---------------- Render ----------------
	return (
		<main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<Heading />

			<section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<CreateCard
					register={createForm.register}
					onSubmit={createForm.handleSubmit(onCreate)}
					errors={{
						slug: createForm.formState.errors.slug?.message,
						name: createForm.formState.errors.name?.message,
						policy: createForm.formState.errors.policy?.message,
					}}
					busy={createMutation.isPending}
				/>
				<AttachCard
					register={attachForm.register}
					onSubmit={attachForm.handleSubmit(onAttach)}
					errors={{
						offer_id: attachForm.formState.errors.offer_id?.message,
						weight: attachForm.formState.errors.weight?.message,
						payout_cents: attachForm.formState.errors.payout_cents?.message,
						active: attachForm.formState.errors.active?.message,
						eligibility_geo: attachForm.formState.errors.eligibility_geo?.message,
						eligibility_device:
							attachForm.formState.errors.eligibility_device?.message,
						eligibility_os: attachForm.formState.errors.eligibility_os?.message,
					}}
					disabled={!selectedId}
					selected={selected}
					busy={attachMutation.isPending}
				/>
			</section>

			<SmartlinksTable
				rows={smartlinks}
				loading={listQuery.isLoading}
				selectedId={selectedId}
				onSelect={(id) => {
					setSelectedId(id);
					setPreview(null);
				}}
				attachments={attachmentsByLink}
				onToggle={(row) =>
					patchMutation.mutate({
						id: row.id,
						input: { active: row.active === 1 ? false : true },
					})
				}
				onDelete={(row) => deleteMutation.mutate(row.id)}
			/>

			<AttachmentsTable
				rows={selectedAttachments}
				selected={selected}
				onRemove={(candidate) => {
					if (!selected) return;
					removeCandidateMutation.mutate({
						smartlinkId: selected.id,
						candidateId: candidate.id,
					});
				}}
			/>

			<TestResolveCard
				register={testForm.register}
				onSubmit={testForm.handleSubmit(onTestResolve)}
				errors={{
					slug: testForm.formState.errors.slug?.message,
					geo: testForm.formState.errors.geo?.message,
					device: testForm.formState.errors.device?.message,
					os: testForm.formState.errors.os?.message,
				}}
				busy={testMutation.isPending}
				result={testResult}
				setSlug={(slug) => testForm.setValue("slug", slug)}
			/>

			<ResolvePreview
				canRun={Boolean(selectedId) && selectedAttachments.length > 0}
				busy={previewMutation.isPending}
				onRun={onPreview}
				distribution={distribution}
				sampleSize={preview?.length ?? 0}
			/>

			<DocHint />
		</main>
	);
}

export default SmartlinksRouterPage;

// ---------------------------------------------------------------------------
// Presentational fragments
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<a
						href="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</a>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">SmartLinks</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Router</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				SmartLinks
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				SmartLink router
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				One short link, many offers. Pick a policy — priority, weighted, or
				payout-max — add candidates with eligibility rules, and every click
				routes to the best-available offer.
			</p>
		</header>
	);
}

function CreateCard(props: {
	register: ReturnType<
		typeof useForm<z.input<typeof createSmartlinkSchema>>
	>["register"];
	onSubmit: (e: React.BaseSyntheticEvent) => Promise<void> | void;
	errors: { slug?: string; name?: string; policy?: string };
	busy: boolean;
}) {
	return (
		<form
			onSubmit={props.onSubmit}
			className={`${CARD} flex flex-col gap-4 p-6`}
			noValidate
		>
			<h2 className="text-lg font-semibold text-zinc-900">Create SmartLink</h2>
			<div className="flex flex-col gap-1.5">
				<label htmlFor="sl-create-slug" className={LABEL}>
					Slug
				</label>
				<input
					id="sl-create-slug"
					type="text"
					autoComplete="off"
					placeholder="e.g. summer-flash"
					className={INPUT}
					aria-invalid={Boolean(props.errors.slug)}
					{...props.register("slug")}
				/>
				<p className={HINT}>
					Lowercase letters, digits, dashes, underscores. 2–64 chars.
				</p>
				{props.errors.slug && <p className={ERROR_TEXT}>{props.errors.slug}</p>}
			</div>
			<div className="flex flex-col gap-1.5">
				<label htmlFor="sl-create-name" className={LABEL}>
					Display name
				</label>
				<input
					id="sl-create-name"
					type="text"
					autoComplete="off"
					placeholder="e.g. Summer Flash 2026"
					className={INPUT}
					aria-invalid={Boolean(props.errors.name)}
					{...props.register("name")}
				/>
				{props.errors.name && <p className={ERROR_TEXT}>{props.errors.name}</p>}
			</div>
			<div className="flex flex-col gap-1.5">
				<label htmlFor="sl-create-policy" className={LABEL}>
					Selection policy
				</label>
				<select
					id="sl-create-policy"
					className={INPUT}
					{...props.register("policy")}
				>
					{SMARTLINK_POLICIES.map((p) => (
						<option key={p} value={p}>
							{formatPolicyLabel(p)}
						</option>
					))}
				</select>
				<p className={HINT}>
					Priority picks the highest-weight candidate. Weighted samples
					randomly by weight. Payout-max picks the highest-earning offer.
				</p>
				{props.errors.policy && (
					<p className={ERROR_TEXT}>{props.errors.policy}</p>
				)}
			</div>
			<div className="flex items-center justify-end">
				<button type="submit" disabled={props.busy} className={BTN_PRIMARY}>
					{props.busy ? "Creating." : "Create SmartLink"}
				</button>
			</div>
		</form>
	);
}

function AttachCard(props: {
	register: ReturnType<
		typeof useForm<z.input<typeof attachCandidateSchema>>
	>["register"];
	onSubmit: (e: React.BaseSyntheticEvent) => Promise<void> | void;
	errors: {
		offer_id?: string;
		weight?: string;
		payout_cents?: string;
		active?: string;
		eligibility_geo?: string;
		eligibility_device?: string;
		eligibility_os?: string;
	};
	disabled: boolean;
	selected: SmartLinkRow | null;
	busy: boolean;
}) {
	return (
		<form
			onSubmit={props.onSubmit}
			className={`${CARD} flex flex-col gap-4 p-6`}
			noValidate
		>
			<div className="flex items-start justify-between gap-2">
				<h2 className="text-lg font-semibold text-zinc-900">Attach candidate</h2>
				{props.selected ? (
					<span className="rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
						{props.selected.slug} ·{" "}
						{formatPolicyLabel(props.selected.policy)}
					</span>
				) : (
					<span className={HINT}>Select a smartlink first.</span>
				)}
			</div>
			<div className="flex flex-col gap-1.5">
				<label htmlFor="sl-attach-offer" className={LABEL}>
					Offer ID
				</label>
				<input
					id="sl-attach-offer"
					type="text"
					autoComplete="off"
					placeholder="e.g. off_1a2b3c"
					className={INPUT}
					disabled={props.disabled}
					aria-invalid={Boolean(props.errors.offer_id)}
					{...props.register("offer_id")}
				/>
				{props.errors.offer_id && (
					<p className={ERROR_TEXT}>{props.errors.offer_id}</p>
				)}
			</div>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<div className="flex flex-col gap-1.5">
					<label htmlFor="sl-attach-weight" className={LABEL}>
						Weight
					</label>
					<input
						id="sl-attach-weight"
						type="number"
						inputMode="numeric"
						min={1}
						max={MAX_WEIGHT}
						step={1}
						className={INPUT}
						disabled={props.disabled}
						aria-invalid={Boolean(props.errors.weight)}
						{...props.register("weight", { valueAsNumber: true })}
					/>
					<p className={HINT}>
						Used by priority + weighted policies.
					</p>
					{props.errors.weight && (
						<p className={ERROR_TEXT}>{props.errors.weight}</p>
					)}
				</div>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="sl-attach-payout" className={LABEL}>
						Payout (cents)
					</label>
					<input
						id="sl-attach-payout"
						type="number"
						inputMode="numeric"
						min={0}
						max={MAX_PAYOUT_CENTS}
						step={1}
						className={INPUT}
						disabled={props.disabled}
						aria-invalid={Boolean(props.errors.payout_cents)}
						{...props.register("payout_cents", { valueAsNumber: true })}
					/>
					<p className={HINT}>Used by payout-max policy.</p>
					{props.errors.payout_cents && (
						<p className={ERROR_TEXT}>{props.errors.payout_cents}</p>
					)}
				</div>
			</div>
			<fieldset className="grid grid-cols-1 gap-4 rounded-[6px] border border-zinc-200 p-4 sm:grid-cols-3">
				<legend className="px-1 text-xs font-medium uppercase tracking-wider text-zinc-500">
					Eligibility (comma lists, blank = any)
				</legend>
				<div className="flex flex-col gap-1">
					<label htmlFor="sl-attach-geo" className={LABEL}>
						Geo
					</label>
					<input
						id="sl-attach-geo"
						type="text"
						placeholder="US, GB, DE"
						className={INPUT}
						disabled={props.disabled}
						{...props.register("eligibility_geo")}
					/>
					{props.errors.eligibility_geo && (
						<p className={ERROR_TEXT}>{props.errors.eligibility_geo}</p>
					)}
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="sl-attach-device" className={LABEL}>
						Device
					</label>
					<input
						id="sl-attach-device"
						type="text"
						placeholder="mobile, desktop"
						className={INPUT}
						disabled={props.disabled}
						{...props.register("eligibility_device")}
					/>
					{props.errors.eligibility_device && (
						<p className={ERROR_TEXT}>{props.errors.eligibility_device}</p>
					)}
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="sl-attach-os" className={LABEL}>
						OS
					</label>
					<input
						id="sl-attach-os"
						type="text"
						placeholder="ios, android"
						className={INPUT}
						disabled={props.disabled}
						{...props.register("eligibility_os")}
					/>
					{props.errors.eligibility_os && (
						<p className={ERROR_TEXT}>{props.errors.eligibility_os}</p>
					)}
				</div>
			</fieldset>
			<div className="flex items-center gap-2">
				<input
					id="sl-attach-active"
					type="checkbox"
					disabled={props.disabled}
					className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
					{...props.register("active")}
				/>
				<label htmlFor="sl-attach-active" className={LABEL}>
					Active
				</label>
			</div>
			<div className="flex items-center justify-end">
				<button
					type="submit"
					disabled={props.disabled || props.busy}
					className={BTN_PRIMARY}
				>
					{props.busy ? "Attaching." : "Attach candidate"}
				</button>
			</div>
		</form>
	);
}

function SmartlinksTable(props: {
	rows: SmartLinkRow[];
	loading: boolean;
	attachments: Record<string, SmartLinkCandidateRow[]>;
	selectedId: string | null;
	onSelect: (id: string) => void;
	onToggle: (row: SmartLinkRow) => void;
	onDelete: (row: SmartLinkRow) => void;
}) {
	if (props.loading) {
		return (
			<section className={`${CARD} p-6`}>
				<h2 className="text-lg font-semibold text-zinc-900">SmartLinks</h2>
				<p className="mt-2 text-sm text-zinc-500">Loading.</p>
			</section>
		);
	}
	if (props.rows.length === 0) {
		return (
			<section className={`${CARD} p-6`}>
				<h2 className="text-lg font-semibold text-zinc-900">SmartLinks</h2>
				<p className="mt-2 text-sm text-zinc-500">
					No smartlinks yet. Create one on the left to get started.
				</p>
			</section>
		);
	}
	return (
		<section className={`${CARD} overflow-hidden`}>
			<header className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
				<h2 className="text-lg font-semibold text-zinc-900">SmartLinks</h2>
				<span className={HINT}>{props.rows.length} total</span>
			</header>
			<div className="overflow-x-auto">
				<table className="min-w-full text-left text-sm">
					<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
						<tr>
							<th scope="col" className="px-6 py-3 font-semibold">
								Slug
							</th>
							<th scope="col" className="px-6 py-3 font-semibold">
								Name
							</th>
							<th scope="col" className="px-6 py-3 font-semibold">
								Policy
							</th>
							<th scope="col" className="px-6 py-3 font-semibold">
								Candidates
							</th>
							<th scope="col" className="px-6 py-3 font-semibold">
								Status
							</th>
							<th scope="col" className="px-6 py-3 font-semibold">
								<span className="sr-only">Actions</span>
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-zinc-200">
						{props.rows.map((row) => {
							const isSelected = props.selectedId === row.id;
							const count = props.attachments[row.id]?.length ?? 0;
							return (
								<tr
									key={row.id}
									className={
										isSelected ? "bg-emerald-50/50" : "hover:bg-zinc-50"
									}
								>
									<td className="px-6 py-3 font-mono text-xs text-zinc-700">
										{row.slug}
									</td>
									<td className="px-6 py-3 text-zinc-900">{row.name}</td>
									<td className="px-6 py-3 text-zinc-700">
										{formatPolicyLabel(row.policy)}
									</td>
									<td className="px-6 py-3 text-zinc-700">{count}</td>
									<td className="px-6 py-3">
										<StatusBadge active={row.active === 1} />
									</td>
									<td className="px-6 py-3 text-right">
										<div className="flex items-center justify-end gap-2">
											<button
												type="button"
												onClick={() => props.onSelect(row.id)}
												className={BTN_SECONDARY}
												aria-pressed={isSelected}
											>
												{isSelected ? "Selected" : "Select"}
											</button>
											<button
												type="button"
												onClick={() => props.onToggle(row)}
												className={BTN_SECONDARY}
											>
												{row.active === 1 ? "Pause" : "Resume"}
											</button>
											<button
												type="button"
												onClick={() => props.onDelete(row)}
												className={BTN_DANGER}
											>
												Delete
											</button>
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</section>
	);
}

function AttachmentsTable(props: {
	rows: SmartLinkCandidateRow[];
	selected: SmartLinkRow | null;
	onRemove: (candidate: SmartLinkCandidateRow) => void;
}) {
	return (
		<section className={`${CARD} overflow-hidden`}>
			<header className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
				<div>
					<h2 className="text-lg font-semibold text-zinc-900">Candidates</h2>
					<p className={HINT}>
						{props.selected
							? `Offers attached to ${props.selected.slug}`
							: "Select a smartlink to view its candidates."}
					</p>
				</div>
				<span className={HINT}>{props.rows.length} attached</span>
			</header>
			{props.rows.length === 0 ? (
				<p className="px-6 py-6 text-sm text-zinc-500">
					No candidates attached yet.
				</p>
			) : (
				<div className="overflow-x-auto">
					<table className="min-w-full text-left text-sm">
						<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
							<tr>
								<th scope="col" className="px-6 py-3 font-semibold">
									Offer ID
								</th>
								<th scope="col" className="px-6 py-3 font-semibold">
									Weight
								</th>
								<th scope="col" className="px-6 py-3 font-semibold">
									Payout
								</th>
								<th scope="col" className="px-6 py-3 font-semibold">
									Eligibility
								</th>
								<th scope="col" className="px-6 py-3 font-semibold">
									Status
								</th>
								<th scope="col" className="px-6 py-3 font-semibold">
									<span className="sr-only">Actions</span>
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200">
							{props.rows.map((row) => (
								<tr key={row.id} className="hover:bg-zinc-50">
									<td className="px-6 py-3 font-mono text-xs text-zinc-700">
										{row.offer_id}
									</td>
									<td className="px-6 py-3 text-zinc-700">{row.weight}</td>
									<td className="px-6 py-3 text-zinc-700">
										{(row.payout_cents / 100).toFixed(2)}
									</td>
									<td className="px-6 py-3 text-xs text-zinc-600">
										{formatEligibility(row.eligibility)}
									</td>
									<td className="px-6 py-3">
										<StatusBadge active={row.active === 1} />
									</td>
									<td className="px-6 py-3 text-right">
										<button
											type="button"
											onClick={() => props.onRemove(row)}
											disabled={row.active !== 1}
											className={BTN_SECONDARY}
										>
											Pause
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function StatusBadge({ active }: { active: boolean }) {
	if (active) {
		return (
			<span className="inline-flex items-center gap-1.5 rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
				<span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
				Active
			</span>
		);
	}
	return (
		<span className="inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-600">
			<span className="h-1.5 w-1.5 rounded-full bg-zinc-400" />
			Paused
		</span>
	);
}

function TestResolveCard(props: {
	register: ReturnType<
		typeof useForm<z.input<typeof resolveContextSchema>>
	>["register"];
	onSubmit: (e: React.BaseSyntheticEvent) => Promise<void> | void;
	errors: { slug?: string; geo?: string; device?: string; os?: string };
	busy: boolean;
	result: ResolveResponse | null;
	setSlug: (slug: string) => void;
}) {
	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<div>
				<h2 className="text-lg font-semibold text-zinc-900">Test resolve</h2>
				<p className={HINT}>
					POST /resolve with a slug and an optional context. Shows the picked
					candidate and redirect URL.
				</p>
			</div>
			<form onSubmit={props.onSubmit} className="flex flex-col gap-4" noValidate>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="sl-test-slug" className={LABEL}>
						Slug
					</label>
					<input
						id="sl-test-slug"
						type="text"
						autoComplete="off"
						placeholder="e.g. summer-flash"
						className={INPUT}
						aria-invalid={Boolean(props.errors.slug)}
						{...props.register("slug")}
					/>
					{props.errors.slug && (
						<p className={ERROR_TEXT}>{props.errors.slug}</p>
					)}
				</div>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					<div className="flex flex-col gap-1">
						<label htmlFor="sl-test-geo" className={LABEL}>
							Geo
						</label>
						<input
							id="sl-test-geo"
							type="text"
							placeholder="US"
							className={INPUT}
							{...props.register("geo")}
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="sl-test-device" className={LABEL}>
							Device
						</label>
						<input
							id="sl-test-device"
							type="text"
							placeholder="mobile"
							className={INPUT}
							{...props.register("device")}
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="sl-test-os" className={LABEL}>
							OS
						</label>
						<input
							id="sl-test-os"
							type="text"
							placeholder="ios"
							className={INPUT}
							{...props.register("os")}
						/>
					</div>
				</div>
				<div className="flex items-center justify-end">
					<button type="submit" disabled={props.busy} className={BTN_PRIMARY}>
						{props.busy ? "Resolving." : "Resolve"}
					</button>
				</div>
			</form>
			{props.result ? (
				<div className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700">
					<div>
						<span className="font-medium">Offer:</span>{" "}
						<span className="font-mono text-xs">
							{props.result.offer_id ?? props.result.picked.offer_id}
						</span>
					</div>
					<div>
						<span className="font-medium">Policy:</span>{" "}
						{props.result.smartlink.policy
							? formatPolicyLabel(props.result.smartlink.policy)
							: "n/a"}
					</div>
					{props.result.redirect_url ? (
						<div>
							<span className="font-medium">Redirect:</span>{" "}
							<span className="font-mono text-xs">
								{props.result.redirect_url}
							</span>
						</div>
					) : null}
					<div>
						<span className="font-medium">Pool size:</span>{" "}
						{props.result.pool_size}
					</div>
				</div>
			) : null}
		</section>
	);
}

function ResolvePreview(props: {
	canRun: boolean;
	busy: boolean;
	onRun: () => void;
	distribution: Array<{ offer_id: string; count: number; percent: number }>;
	sampleSize: number;
}) {
	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<header className="flex items-start justify-between gap-2">
				<div>
					<h2 className="text-lg font-semibold text-zinc-900">
						Distribution preview
					</h2>
					<p className={HINT}>
						Fires {RESOLVE_SAMPLE_SIZE} resolves to sample the weighted
						distribution (legacy GET /:id/resolve).
					</p>
				</div>
				<button
					type="button"
					onClick={props.onRun}
					disabled={!props.canRun || props.busy}
					className={BTN_DANGER}
				>
					{props.busy ? "Resolving." : "Run preview"}
				</button>
			</header>
			{props.distribution.length === 0 ? (
				<p className="text-sm text-zinc-500">
					{props.sampleSize === 0
						? "No preview yet. Click Run preview."
						: "Preview returned no picks."}
				</p>
			) : (
				<div className="overflow-x-auto">
					<table className="min-w-full text-left text-sm">
						<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
							<tr>
								<th scope="col" className="px-6 py-3 font-semibold">
									Offer ID
								</th>
								<th scope="col" className="px-6 py-3 font-semibold">
									Picks
								</th>
								<th scope="col" className="px-6 py-3 font-semibold">
									Share
								</th>
								<th scope="col" className="px-6 py-3 font-semibold">
									<span className="sr-only">Bar</span>
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200">
							{props.distribution.map((row) => (
								<tr key={row.offer_id}>
									<td className="px-6 py-3 font-mono text-xs text-zinc-700">
										{row.offer_id}
									</td>
									<td className="px-6 py-3 text-zinc-900">{row.count}</td>
									<td className="px-6 py-3 text-zinc-700">{row.percent}%</td>
									<td className="px-6 py-3">
										<div className="h-2 w-40 overflow-hidden rounded-[6px] bg-zinc-100">
											<div
												className="h-full rounded-[6px] bg-gradient-to-r from-emerald-500 to-sky-500"
												style={{ width: `${Math.max(row.percent, 2)}%` }}
											/>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How it works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					<strong>Priority</strong> routes every click to the highest-weight
					eligible candidate.
				</li>
				<li>
					<strong>Weighted</strong> picks randomly with probability
					proportional to weight.
				</li>
				<li>
					<strong>Payout-max</strong> routes to the candidate with the highest
					payout_cents.
				</li>
				<li>
					Eligibility is an allow-list per dimension. Empty = any. Match is
					case-insensitive.
				</li>
			</ul>
		</aside>
	);
}
